'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  getFavorites,
  getFavoriteIds,
  addFavorite,
  removeFavorite,
  setFavorites,
  clearFavorites,
  isFavorite as checkIsFavorite,
  FavoriteItem,
} from '@/lib/FavoritesStorage';
import { getIsAnonymous, getLocalUser } from '@/lib/LocalStorage';
import { WindowDispatchEvent } from '@/lib/WindowDispatchEvent';
import { WebLink } from '@/lib/WebLinks';
import { Requester } from '@/lib/Requester';
import { Endpoints } from '@/lib/Endpoints';
import { GenericResponse } from '@/models/GenericResponse';
import { ProductDetail } from '@/models/Product';

type FavoriteApiItem = {
  productId?: number;
  id?: number;
  title?: string;
  url?: string;
  imageUrl?: string;
};

type FavoritesApiResponse = {
  productIds?: number[];
  favorites?: FavoriteApiItem[];
};

type ProductBatchResponse = {
  product?: ProductDetail['product'][];
};

let favoritesHydrationPromise: Promise<void> | null = null;
let favoritesHydrated = false;
let favoritesHydratedUserId: string | number | null = null;
let favoritesHydrationVersion = 0;
let favoritesAuthListenerRegistered = false;
const pendingAddIds = new Set<number>();
const pendingRemoveIds = new Set<number>();

const getFavoritesUserId = () => getLocalUser()?.id ?? null;

const resetFavoritesHydration = () => {
  favoritesHydrationVersion += 1;
  favoritesHydrationPromise = null;
  favoritesHydrated = false;
  favoritesHydratedUserId = null;
  pendingAddIds.clear();
  pendingRemoveIds.clear();
};

const normalizeFavoriteItems = (
  payload?: FavoritesApiResponse,
  fallbackItems?: FavoriteItem[],
): FavoriteItem[] => {
  if (!payload) return [];

  if (Array.isArray(payload.favorites) && payload.favorites.length > 0) {
    return payload.favorites
      .map((item) => {
        const id = Number(item.productId ?? item.id);
        if (!Number.isFinite(id) || id <= 0) return null;

        return {
          id,
          title: item.title || '',
          url: item.url || '',
          imageUrl: item.imageUrl || '',
        } satisfies FavoriteItem;
      })
      .filter((item): item is FavoriteItem => Boolean(item));
  }

  const serverIds = Array.isArray(payload.productIds) ? payload.productIds : [];
  const effectiveIds = Array.from(
    new Set([...serverIds.filter((id) => !pendingRemoveIds.has(id)), ...Array.from(pendingAddIds)]),
  );
  const current = fallbackItems ?? getFavorites();
  const currentById = new Map(current.map((item) => [item.id, item]));
  return effectiveIds
    .map((id) => {
      const existing = currentById.get(id);
      if (existing) {
        return existing;
      }

      return {
        id,
        title: '',
        url: '',
        imageUrl: '',
      } satisfies FavoriteItem;
    })
    .filter((item): item is FavoriteItem => Boolean(item));
};

const enrichFavoritesWithProducts = async (items: FavoriteItem[]): Promise<FavoriteItem[]> => {
  const missingIds = items
    .filter((item) => !item.title || !item.url || !item.imageUrl)
    .map((item) => item.id);
  if (missingIds.length === 0) {
    return items;
  }

  let detailsById = new Map<number, FavoriteItem | null>();
  try {
    const params =
      missingIds.length === 1 ? { productId: missingIds[0] } : { 'productIds[]': missingIds };

    const { data: result } = await Requester.get<GenericResponse<ProductBatchResponse>>(
      Endpoints.PRODUCT,
      {
        params,
        paramsSerializer: {
          indexes: null,
        },
      },
    );

    const products = result.data?.product ?? [];
    detailsById = new Map(
      products.map((product) => [
        product.id,
        {
          id: product.id,
          title: product.title || `Favori Ürün #${product.id}`,
          url: product.url || `/urun/${product.id}`,
          imageUrl: product.imageUrl || '',
        } satisfies FavoriteItem,
      ]),
    );
  } catch {
    detailsById = new Map(missingIds.map((productId) => [productId, null]));
  }

  return items
    .map((item) => detailsById.get(item.id) ?? item)
    .filter((item): item is FavoriteItem => Boolean(item?.title && item.url && item.imageUrl));
};

const applyServerFavoritesToStorage = async (
  payload?: FavoritesApiResponse,
  fallbackItems?: FavoriteItem[],
) => {
  if (!payload) return;

  const normalized = normalizeFavoriteItems(payload, fallbackItems);
  const enriched = await enrichFavoritesWithProducts(normalized);
  setFavorites(enriched);
};

const hydrateFavoritesFromServer = async () => {
  const currentUserId = getFavoritesUserId();
  const hydrationVersion = favoritesHydrationVersion;

  if (getIsAnonymous() || currentUserId === null) {
    favoritesHydrated = false;
    favoritesHydratedUserId = null;
    return;
  }

  if (favoritesHydrated && favoritesHydratedUserId === currentUserId) {
    return;
  }

  if (!favoritesHydrationPromise) {
    favoritesHydrationPromise = (async () => {
      try {
        const { data: result } = await Requester.get<GenericResponse<FavoritesApiResponse>>(
          Endpoints.GET_FAVORITES,
        );
        if (
          hydrationVersion !== favoritesHydrationVersion ||
          getIsAnonymous() ||
          getFavoritesUserId() !== currentUserId
        ) {
          return;
        }
        await applyServerFavoritesToStorage(result.data);
        favoritesHydrated = true;
        favoritesHydratedUserId = currentUserId;
      } finally {
        favoritesHydrationPromise = null;
      }
    })();
  }

  await favoritesHydrationPromise;
};

const ensureFavoritesAuthListener = () => {
  if (favoritesAuthListenerRegistered || typeof window === 'undefined') {
    return;
  }

  const handleAuthChange = () => {
    resetFavoritesHydration();
    if (getIsAnonymous()) {
      clearFavorites();
      return;
    }
    void hydrateFavoritesFromServer();
  };

  window.addEventListener(WindowDispatchEvent.UPDATE_USER, handleAuthChange);
  window.addEventListener(WindowDispatchEvent.UPDATE_ANONYMOUS_STATE, handleAuthChange);
  favoritesAuthListenerRegistered = true;
};

export function useFavorites() {
  const [favorites, setFavoritesState] = useState<FavoriteItem[]>([]);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRemoveRef = useRef<FavoriteItem | null>(null);

  const sync = useCallback(() => {
    setFavoritesState(getFavorites());
  }, []);

  const applyServerFavorites = useCallback(
    async (payload?: FavoritesApiResponse, fallbackItems?: FavoriteItem[]) => {
      if (!payload) {
        sync();
        return;
      }
      await applyServerFavoritesToStorage(payload, fallbackItems);
      favoritesHydrated = true;
      favoritesHydratedUserId = getFavoritesUserId();
    },
    [sync],
  );

  const syncFromServer = useCallback(async () => {
    if (getIsAnonymous()) {
      sync();
      return;
    }

    try {
      await hydrateFavoritesFromServer();
      sync();
    } catch (e) {
      sync();
    }
  }, [sync]);

  useEffect(() => {
    ensureFavoritesAuthListener();
    sync();
    window.addEventListener(WindowDispatchEvent.UPDATE_FAVORITES, sync);
    return () => {
      window.removeEventListener(WindowDispatchEvent.UPDATE_FAVORITES, sync);
    };
  }, [sync]);

  useEffect(() => {
    syncFromServer();
  }, [syncFromServer]);

  const favoriteIds = favorites.map((f) => f.id);

  const isFavorite = useCallback(
    (productId: number) => favoriteIds.includes(productId),
    [favoriteIds],
  );

  const add = useCallback(
    async (item: FavoriteItem): Promise<boolean> => {
      if (getIsAnonymous()) {
        try {
          sessionStorage.setItem('hzc_login_dest', window.location.pathname + window.location.search);
        } catch {}
        window.location.href = WebLink.LOGIN;
        return false;
      }

      // Optimistic UI
      addFavorite(item);
      pendingAddIds.add(item.id);
      pendingRemoveIds.delete(item.id);

      try {
        const { data: result } = await Requester.post<GenericResponse<FavoritesApiResponse>>(
          Endpoints.ADD_FAVORITE,
          { productId: item.id },
        );
        await applyServerFavorites(result.data, getFavorites());
        pendingAddIds.delete(item.id);
      } catch (e) {
        pendingAddIds.delete(item.id);
        removeFavorite(item.id);
        return false;
      }

      return true;
    },
    [applyServerFavorites],
  );

  const remove = useCallback(
    (productId: number): { undo: () => void } => {
      // Save the item before removing for undo
      const itemToRemove = getFavorites().find((f) => f.id === productId);

      // Clear any previous pending remove timer
      if (undoTimerRef.current) {
        clearTimeout(undoTimerRef.current);
      }

      // Optimistic UI — remove immediately
      removeFavorite(productId);
      pendingRemoveIds.add(productId);
      pendingAddIds.delete(productId);
      pendingRemoveRef.current = itemToRemove || null;

      const undo = () => {
        if (undoTimerRef.current) {
          clearTimeout(undoTimerRef.current);
          undoTimerRef.current = null;
        }
        if (pendingRemoveRef.current) {
          // Re-add to favorites
          addFavorite(pendingRemoveRef.current);
          pendingRemoveIds.delete(pendingRemoveRef.current.id);
        }
        pendingRemoveRef.current = null;
      };

      // After 5 seconds, send the actual delete to backend
      undoTimerRef.current = setTimeout(async () => {
        pendingRemoveRef.current = null;
        undoTimerRef.current = null;

        try {
          const { data: result } = await Requester.post<GenericResponse<FavoritesApiResponse>>(
            Endpoints.REMOVE_FAVORITE,
            { productId },
          );
          await applyServerFavorites(result.data, getFavorites());
          pendingRemoveIds.delete(productId);
        } catch (e) {
          pendingRemoveIds.delete(productId);
          if (itemToRemove) addFavorite(itemToRemove);
        }
      }, 5000);

      return { undo };
    },
    [applyServerFavorites],
  );

  const toggle = useCallback(
    (item: FavoriteItem): { added: boolean; undo?: () => void } => {
      if (checkIsFavorite(item.id)) {
        const { undo } = remove(item.id);
        return { added: false, undo };
      } else {
        add(item);
        return { added: true };
      }
    },
    [add, remove],
  );

  return {
    favorites,
    favoriteIds,
    isFavorite,
    add,
    remove,
    toggle,
    count: favorites.length,
  };
}
